import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import type { Team } from '@/types/entities'

interface TeamViewDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    team: Team | null
}

export function TeamViewDialog({ open, onOpenChange, team }: TeamViewDialogProps) {
    const { t } = useTranslation()
    if (!team) return null

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{t('teams.viewTeam')}</DialogTitle>
                    <DialogDescription>
                        {t('teams.viewTeamDescription')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div>
                        <label className="text-sm font-medium text-muted-foreground">
                            {t('teams.teamName')}
                        </label>
                        <p className="text-sm mt-1">{team.teamName}</p>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-muted-foreground">
                            {t('teams.description')}
                        </label>
                        <p className="text-sm mt-1">{team.description || '-'}</p>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-muted-foreground">
                            {t('teams.mobilisation')}
                        </label>
                        <div className="mt-1">
                            <Badge variant={team.mobilisation ? 'default' : 'secondary'}>
                                {team.mobilisation ? t('common.yes') : t('common.no')}
                            </Badge>
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-muted-foreground">
                            {t('teams.task')}
                        </label>
                        <div className="mt-1">
                            <Badge variant={team.task ? 'default' : 'secondary'}>
                                {team.task ? t('common.yes') : t('common.no')}
                            </Badge>
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-muted-foreground">
                            {t('teams.sms')}
                        </label>
                        <div className="mt-1 p-3 bg-muted rounded-md">
                            <p className="text-sm whitespace-pre-wrap">{team.sms || '-'}</p>
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-muted-foreground">
                            {t('teams.email')}
                        </label>
                        <div className="mt-1 p-3 bg-muted rounded-md border overflow-auto max-h-48">
                            {team.email ? (
                                <div 
                                    className="text-sm [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-2 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-2 [&_strong]:font-bold [&_em]:italic [&_u]:underline"
                                    dangerouslySetInnerHTML={{ __html: team.email }}
                                />
                            ) : (
                                <p className="text-sm text-muted-foreground">-</p>
                            )}
                        </div>
                    </div>

                    {team.createdAt && (
                        <div>
                            <label className="text-sm font-medium text-muted-foreground">
                                {t('teams.createdAt')}
                            </label>
                            <p className="text-sm mt-1">
                                {new Date(team.createdAt).toLocaleString()}
                            </p>
                        </div>
                    )}

                    {team.updatedAt && (
                        <div>
                            <label className="text-sm font-medium text-muted-foreground">
                                {t('teams.updatedAt')}
                            </label>
                            <p className="text-sm mt-1">
                                {new Date(team.updatedAt).toLocaleString()}
                            </p>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}
